import type { ProblemJSDependencies } from '../../problemJSDependencies';
import { parseXML } from '../../utils/parseXML';
import { Line } from '../Line';
import { buildMulti } from '../Multi';

export interface ToLineParams {
  problemJS: ProblemJSDependencies;
  xml: string;
  x?: number;
  y?: number;
  multiline?: boolean;
  lockViewportDim?: boolean;
  font?: string;
  editable?: boolean;
  maxW?: number;
}

export const toLine = (params: ToLineParams): Line => {
  const { problemJS, x = 0, y = 0, editable = true } = params;

  const lineWithoutContents = new Line(problemJS, x, y);

  lineWithoutContents.setEditable(editable);

  return injectLineContents(lineWithoutContents, params);
};

/**
 * Inject contents (Multi) into a Line.
 *
 * @param line An 'empty' Line object to inject contents into
 * @param params Parameters for building the Line's contents
 * @returns The Line that was passed in, now with contents injected
 */
export const injectLineContents = <T extends Line>(
  line: T,
  params: ToLineParams
): T => {
  const { problemJS, xml, multiline, font, lockViewportDim, maxW } = params;

  const multi =
    xml === undefined
      ? buildMulti(problemJS, line)
      : parseXML({
          problemJS,
          line,
          val: xml,
          first: 0,
          last: xml.length - 1,
          multiline,
          maxTextW: maxW,
        });

  // Replace default multi
  line.children.pop();
  multi.parent = line;
  line.children.push(multi);
  line.multi = multi;

  if (font) line.setFont(font);

  line.balanceEmptyTexts();
  line.buildSizeRecursive();

  line.viewportW = line.width;
  line.viewportH = line.height;
  line.lockViewportDim = lockViewportDim ?? true;

  return line;
};
